import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { referenceLayersApi } from "@/services/reference-layers-api.service";
import { organizationsApi, rolesApi } from "@/services/api-v2.service";
import { useToast } from "@/hooks/use-toast";
import type { ReferenceLayer, ReferenceLayerProbeResult } from "@/types/reference-layer";

const emptyForm = {
  code: "",
  title: "",
  description: "",
  geoserver_workspace: "ref",
  geoserver_layer: "",
  default_opacity: 0.85,
  sort_order: 0,
  is_active: true,
};

function layerToForm(layer: ReferenceLayer) {
  return {
    code: layer.code,
    title: layer.title,
    description: layer.description ?? "",
    geoserver_workspace: layer.geoserver_workspace,
    geoserver_layer: layer.geoserver_layer,
    default_opacity: layer.default_opacity,
    sort_order: layer.sort_order,
    is_active: layer.is_active,
  };
}

export function ReferenceLayerManagement() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [form, setForm] = useState(emptyForm);
  const [editForm, setEditForm] = useState(emptyForm);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [policyOrgId, setPolicyOrgId] = useState<string>("");
  const [policyRoleId, setPolicyRoleId] = useState<string>("");
  const [policyUserId, setPolicyUserId] = useState("");
  const [probeResult, setProbeResult] = useState<ReferenceLayerProbeResult | null>(null);
  const [layerToDelete, setLayerToDelete] = useState<ReferenceLayer | null>(null);

  const { data: layers = [], isLoading } = useQuery({
    queryKey: ["reference-layers", "admin"],
    queryFn: () => referenceLayersApi.adminList(),
  });
  const { data: orgs = [] } = useQuery({
    queryKey: ["organizations", "ref-layers-admin"],
    queryFn: () => organizationsApi.list(),
  });
  const { data: roles = [] } = useQuery({
    queryKey: ["roles", "ref-layers-admin"],
    queryFn: () => rolesApi.list(),
  });
  const { data: policies = [] } = useQuery({
    queryKey: ["reference-layers", "policies", selectedId],
    queryFn: () => referenceLayersApi.listPolicies(selectedId!),
    enabled: selectedId != null,
  });

  const selected = useMemo(
    () => layers.find((l) => l.id === selectedId) ?? null,
    [layers, selectedId],
  );

  useEffect(() => {
    if (selected) {
      setEditForm(layerToForm(selected));
      setProbeResult(null);
    }
  }, [selected]);

  const createMut = useMutation({
    mutationFn: () =>
      referenceLayersApi.adminCreate({
        ...form,
        description: form.description || null,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["reference-layers"] });
      setForm(emptyForm);
      toast({ title: "Слой создан" });
    },
    onError: (e: Error) => toast({ title: "Ошибка", description: e.message, variant: "destructive" }),
  });

  const saveMut = useMutation({
    mutationFn: () =>
      referenceLayersApi.adminUpdate(selectedId!, {
        ...editForm,
        description: editForm.description || null,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["reference-layers"] });
      toast({ title: "Слой сохранён" });
    },
    onError: (e: Error) => toast({ title: "Ошибка", description: e.message, variant: "destructive" }),
  });

  const toggleActiveMut = useMutation({
    mutationFn: ({ id, is_active }: { id: number; is_active: boolean }) =>
      referenceLayersApi.adminUpdate(id, { is_active }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["reference-layers"] });
    },
  });

  const deleteMut = useMutation({
    mutationFn: (id: number) => referenceLayersApi.adminDelete(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["reference-layers"] });
      setSelectedId(null);
      setProbeResult(null);
      setLayerToDelete(null);
      toast({ title: "Слой удалён" });
    },
    onError: (e: Error) =>
      toast({ title: "Ошибка удаления", description: e.message, variant: "destructive" }),
  });

  const probeMut = useMutation({
    mutationFn: () => referenceLayersApi.adminProbe(selectedId!),
    onSuccess: (result) => {
      setProbeResult(result);
      toast({
        title: result.ok ? "Проверка OK" : "Проверка не прошла",
        description: result.message,
        variant: result.ok ? "default" : "destructive",
      });
    },
    onError: (e: Error) =>
      toast({ title: "Ошибка проверки", description: e.message, variant: "destructive" }),
  });

  const addPolicyMut = useMutation({
    mutationFn: () =>
      referenceLayersApi.createPolicy(selectedId!, {
        organization_id: policyOrgId ? Number(policyOrgId) : null,
        role_id: policyRoleId ? Number(policyRoleId) : null,
        user_id: policyUserId.trim() || null,
        can_view: true,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["reference-layers", "policies", selectedId] });
      setPolicyOrgId("");
      setPolicyRoleId("");
      setPolicyUserId("");
      toast({ title: "Политика добавлена" });
    },
    onError: (e: Error) => toast({ title: "Ошибка", description: e.message, variant: "destructive" }),
  });

  const delPolicyMut = useMutation({
    mutationFn: (policyId: number) =>
      referenceLayersApi.deletePolicy(selectedId!, policyId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["reference-layers", "policies", selectedId] });
    },
  });

  return (
    <div className="space-y-6">
      <AlertDialog
        open={layerToDelete != null}
        onOpenChange={(open) => {
          if (!open) setLayerToDelete(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Удалить справочный слой?</AlertDialogTitle>
            <AlertDialogDescription>
              Слой «{layerToDelete?.title}» ({layerToDelete?.code}) будет удалён из каталога
              приложения. Публикация в GeoServer не затрагивается.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMut.isPending}>Отмена</AlertDialogCancel>
            <AlertDialogAction
              disabled={deleteMut.isPending || layerToDelete == null}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={(e) => {
                e.preventDefault();
                if (layerToDelete) deleteMut.mutate(layerToDelete.id);
              }}
            >
              {deleteMut.isPending ? "Удаление…" : "Удалить"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Card>
        <CardHeader>
          <CardTitle>Справочные слои (GeoServer)</CardTitle>
          <CardDescription>
            Каталог оверлеев только для просмотра. Выберите слой, чтобы править поля или проверить связь с GeoServer.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Загрузка…</p>
          ) : (
            <ul className="space-y-2">
              {layers.map((layer: ReferenceLayer) => (
                <li
                  key={layer.id}
                  className={`flex flex-wrap items-center justify-between gap-2 rounded-md border px-3 py-2 ${
                    selectedId === layer.id ? "border-primary bg-muted/40" : ""
                  }`}
                >
                  <button
                    type="button"
                    className="text-left text-sm font-medium hover:underline"
                    onClick={() => setSelectedId(layer.id)}
                  >
                    {layer.title}{" "}
                    <span className="text-muted-foreground font-normal">
                      ({layer.code} → {layer.geoserver_workspace}:{layer.geoserver_layer})
                    </span>
                  </button>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">Активен</span>
                      <Switch
                        checked={layer.is_active}
                        onCheckedChange={(v) =>
                          toggleActiveMut.mutate({ id: layer.id, is_active: v })
                        }
                      />
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => setLayerToDelete(layer)}
                    >
                      Удалить
                    </Button>
                  </div>
                </li>
              ))}
              {layers.length === 0 && (
                <p className="text-sm text-muted-foreground">Слоёв пока нет — создайте ниже.</p>
              )}
            </ul>
          )}
        </CardContent>
      </Card>

      {selected && (
        <Card>
          <CardHeader>
            <CardTitle>Редактирование: {selected.title}</CardTitle>
            <CardDescription>
              Исправьте workspace/layer или название без удаления записи. «Проверить» — запрос к GeoServer WMS.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-3 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Код</Label>
              <Input
                value={editForm.code}
                onChange={(e) => setEditForm((f) => ({ ...f, code: e.target.value }))}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Название</Label>
              <Input
                value={editForm.title}
                onChange={(e) => setEditForm((f) => ({ ...f, title: e.target.value }))}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Workspace GeoServer</Label>
              <Input
                value={editForm.geoserver_workspace}
                onChange={(e) =>
                  setEditForm((f) => ({ ...f, geoserver_workspace: e.target.value }))
                }
              />
            </div>
            <div className="space-y-1.5">
              <Label>Layer GeoServer</Label>
              <Input
                value={editForm.geoserver_layer}
                onChange={(e) =>
                  setEditForm((f) => ({ ...f, geoserver_layer: e.target.value }))
                }
              />
            </div>
            <div className="space-y-1.5">
              <Label>Прозрачность (0…1)</Label>
              <Input
                type="number"
                step="0.05"
                min={0}
                max={1}
                value={editForm.default_opacity}
                onChange={(e) =>
                  setEditForm((f) => ({
                    ...f,
                    default_opacity: Number(e.target.value),
                  }))
                }
              />
            </div>
            <div className="space-y-1.5">
              <Label>Порядок</Label>
              <Input
                type="number"
                value={editForm.sort_order}
                onChange={(e) =>
                  setEditForm((f) => ({ ...f, sort_order: Number(e.target.value) }))
                }
              />
            </div>
            <div className="md:col-span-2 space-y-1.5">
              <Label>Описание</Label>
              <Textarea
                value={editForm.description}
                onChange={(e) =>
                  setEditForm((f) => ({ ...f, description: e.target.value }))
                }
                rows={2}
              />
            </div>
            <div className="md:col-span-2 flex flex-wrap items-center gap-3">
              <Button
                type="button"
                disabled={
                  !editForm.code
                  || !editForm.title
                  || !editForm.geoserver_layer
                  || saveMut.isPending
                }
                onClick={() => saveMut.mutate()}
              >
                Сохранить
              </Button>
              <Button
                type="button"
                variant="secondary"
                disabled={probeMut.isPending}
                onClick={() => probeMut.mutate()}
              >
                {probeMut.isPending ? "Проверка…" : "Проверить"}
              </Button>
              {probeResult && (
                <p
                  className={`text-sm ${
                    probeResult.ok ? "text-emerald-700" : "text-destructive"
                  }`}
                >
                  [{probeResult.status}] {probeResult.message}
                  {probeResult.bbox ? ` · bbox ${probeResult.bbox}` : ""}
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Новый слой</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3 md:grid-cols-2">
          <div className="space-y-1.5">
            <Label>Код</Label>
            <Input
              value={form.code}
              onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
              placeholder="gelenjyk"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Название</Label>
            <Input
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              placeholder="Геленджик"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Workspace GeoServer</Label>
            <Input
              value={form.geoserver_workspace}
              onChange={(e) =>
                setForm((f) => ({ ...f, geoserver_workspace: e.target.value }))
              }
            />
          </div>
          <div className="space-y-1.5">
            <Label>Layer GeoServer</Label>
            <Input
              value={form.geoserver_layer}
              onChange={(e) =>
                setForm((f) => ({ ...f, geoserver_layer: e.target.value }))
              }
            />
          </div>
          <div className="md:col-span-2 space-y-1.5">
            <Label>Описание</Label>
            <Textarea
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              rows={2}
            />
          </div>
          <div className="md:col-span-2">
            <Button
              type="button"
              disabled={!form.code || !form.title || !form.geoserver_layer || createMut.isPending}
              onClick={() => createMut.mutate()}
            >
              Создать
            </Button>
          </div>
        </CardContent>
      </Card>

      {selected && (
        <Card>
          <CardHeader>
            <CardTitle>Политики просмотра: {selected.title}</CardTitle>
            <CardDescription>
              Укажите организацию, роль или user id (хотя бы одно поле).
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <ul className="space-y-2 text-sm">
              {policies.map((p) => (
                <li
                  key={p.id}
                  className="flex items-center justify-between rounded border px-3 py-2"
                >
                  <span>
                    {p.organization_id != null && `org=${p.organization_id} `}
                    {p.role_id != null && `role=${p.role_id} `}
                    {p.user_id && `user=${p.user_id}`}
                  </span>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => delPolicyMut.mutate(p.id)}
                  >
                    Удалить
                  </Button>
                </li>
              ))}
              {policies.length === 0 && (
                <p className="text-muted-foreground">Политик нет (SuperAdmin всё равно видит активные слои).</p>
              )}
            </ul>
            <div className="grid gap-3 md:grid-cols-3">
              <div className="space-y-1.5">
                <Label>Организация</Label>
                <Select value={policyOrgId || "none"} onValueChange={(v) => setPolicyOrgId(v === "none" ? "" : v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="—" />
                  </SelectTrigger>
                  <SelectContent className="z-[250]">
                    <SelectItem value="none">—</SelectItem>
                    {orgs.map((o) => (
                      <SelectItem key={o.id} value={String(o.id)}>
                        {o.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Роль</Label>
                <Select value={policyRoleId || "none"} onValueChange={(v) => setPolicyRoleId(v === "none" ? "" : v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="—" />
                  </SelectTrigger>
                  <SelectContent className="z-[250]">
                    <SelectItem value="none">—</SelectItem>
                    {roles.map((r) => (
                      <SelectItem key={r.id} value={String(r.id)}>
                        {r.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>User ID</Label>
                <Input
                  value={policyUserId}
                  onChange={(e) => setPolicyUserId(e.target.value)}
                  placeholder="uuid"
                />
              </div>
            </div>
            <Button
              type="button"
              disabled={
                (!policyOrgId && !policyRoleId && !policyUserId.trim())
                || addPolicyMut.isPending
              }
              onClick={() => addPolicyMut.mutate()}
            >
              Добавить политику
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
